import { Fragment, useEffect, useState } from 'react';
import { errorLogAPI } from '../../services/api';

function levelBadge(level) {
  const cls = level === 'error'
    ? 'bg-amber-100 text-amber-700'
    : 'bg-red-100 text-red-700';
  return <span className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase whitespace-nowrap ${cls}`}>{level}</span>;
}

export default function ErrorLogs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [level, setLevel] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [clearing, setClearing] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  function load() {
    setLoading(true);
    errorLogAPI.list(page, 25, level, search)
      .then((r) => {
        setLogs(r.logs || []);
        setTotalPages(r.total_pages || 1);
        setTotal(r.total || 0);
        setCounts(r.counts || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }

  useEffect(load, [page, level, search]);
  useEffect(() => { setPage(1); }, [level, search]);

  async function handleClear() {
    if (!confirm('Clear all error logs? This cannot be undone.')) return;
    setClearing(true);
    try {
      await errorLogAPI.clear();
      load();
    } catch (e) {
      alert(e.response?.data?.error || 'Failed to clear logs.');
    } finally {
      setClearing(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Error Logs</h1>
          <p className="mt-0.5 text-sm text-slate-400">
            System errors captured automatically — failed uploads, exceptions, and other runtime problems.
          </p>
        </div>
        <button onClick={handleClear} disabled={clearing || total === 0}
          className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-40">
          {clearing ? 'Clearing…' : '🗑 Clear All'}
        </button>
      </div>

      {counts.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setLevel('')}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${level === '' ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
            All ({total.toLocaleString()})
          </button>
          {counts.map((c) => (
            <button key={c.level} onClick={() => setLevel(c.level)}
              className={`rounded-full border px-3 py-1.5 text-xs font-semibold uppercase ${level === c.level ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
              {c.level} ({c.count.toLocaleString()})
            </button>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <input type="text" value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Search message or URL…"
          className="w-full max-w-sm rounded-xl border border-slate-200 px-3.5 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-sm text-slate-400">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-1 py-16">
            <p className="text-sm font-medium text-slate-500">No errors logged</p>
            <p className="text-xs text-slate-400">This fills in automatically whenever something breaks.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-100 bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Date &amp; Time</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Level</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">URL</th>
                  <th className="px-4 py-3 w-16" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {logs.map((r) => (
                  <Fragment key={r.id}>
                    <tr className="cursor-pointer transition-colors hover:bg-slate-50"
                      onClick={() => setExpanded(expanded === r.id ? null : r.id)}>
                      <td className="whitespace-nowrap px-4 py-3 font-mono text-xs text-slate-500">{r.created_at}</td>
                      <td className="px-4 py-3">{levelBadge(r.level)}</td>
                      <td className="max-w-md truncate px-4 py-3 text-xs text-slate-700">{r.message}</td>
                      <td className="max-w-xs truncate px-4 py-3 font-mono text-xs text-slate-400">{r.method} {r.url || '—'}</td>
                      <td className="px-4 py-3 text-right">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                          className={`h-4 w-4 text-slate-400 transition-transform ${expanded === r.id ? 'rotate-180' : ''}`}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="m6 9 6 6 6-6" />
                        </svg>
                      </td>
                    </tr>
                    {expanded === r.id && (
                      <tr>
                        <td colSpan={5} className="bg-slate-50 px-4 py-4">
                          <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Full Message</p>
                          <pre className="max-h-80 overflow-y-auto whitespace-pre-wrap break-all rounded-lg border border-slate-200 bg-white p-3 font-mono text-[11px] text-slate-700">
                            {r.message}
                          </pre>
                          <div className="mt-2 flex flex-wrap gap-4 text-xs text-slate-400">
                            <span>URL: <span className="font-mono text-slate-600">{r.method} {r.url || '—'}</span></span>
                            <span>IP: <span className="font-mono text-slate-600">{r.ip_address || '—'}</span></span>
                            <span>User ID: <span className="font-mono text-slate-600">{r.user_id ?? '—'}</span></span>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!loading && logs.length > 0 && (
          <div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
            <p className="text-xs text-slate-400">Page {page} of {totalPages} · {total.toLocaleString()} total</p>
            <div className="flex gap-1.5">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                ← Prev
              </button>
              <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40">
                Next →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
